import React from 'react';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import Container from '../components/grid/Container';
import { t } from '../utils/i18n';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import Paragraph from '../components/elements/Paragraph';
import {
  fetchAllTravellersWithMemories,
  fetchContentPage
} from '../integrations/contentful/queries';
import {
  CmsContentPage,
  instanceOfCmsPopularMemoriesLiftBlock,
  TravellerWithMemories
} from '../integrations/contentful/types';
import Spacing from '../components/common/Spacing';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import StickyCol from '../components/grid/StickyCol';
import { columnTopSpacing } from '../styles/theme';
import DisplayOn from '../components/common/DisplayOn';
import ContentBlockList from '../components/content-blocks/ContentBlockList';
import HorizontalLine from '../components/common/HorizontalLine';
import LabelledCountRow from '../components/list-pages/LabelledCountRow';
import LabelledCount from '../components/list-pages/LabelledCount';
import TravellerListPageCard from '../components/travellers/TravellerListPageCard';
import HeadingWithCount from '../components/common/HeadingWithCount';
import TimMessage from '../components/tim/TimMessage';
import { TimPage } from './TimPage';

interface TravellerListPageProps {
  contentPage?: CmsContentPage;
  travellersWithMemories: TravellerWithMemories[];
}
export const travellerListPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Järjestäjät'),
  path: Paths.TravellerList
};

function renderLabelledCounts(
  travellerCount: number,
  memoryCount: number,
  destinationCount: number
) {
  return (
    <LabelledCountRow>
      <LabelledCount
        data-testid="traveller-count"
        count={travellerCount}
        label={t('järjestäjää')}
      />
      <LabelledCount
        data-testid="memory-count"
        count={memoryCount}
        label={t('matkaa')}
      />
      <LabelledCount
        data-testid="destination-count"
        count={destinationCount}
        label={t('eri kenttää')}
      />
    </LabelledCountRow>
  );
}

const TravellerListPage: TimPage<TravellerListPageProps> = ({
  contentPage,
  travellersWithMemories
}) => {
  if (!contentPage) {
    return null;
  }

  const memories = uniqBy(
    flatten(travellersWithMemories.map((traveller) => traveller.memories)),
    'sys.id'
  );
  const destinations = uniqBy(
    flatten(memories.map((memory) => memory.fields.destinations)),
    'sys.id'
  );

  // Split Contentful model's content block list to two separate instances to accommodate the layout
  // In future consider creating separate Contenful model for these listing pages instead of using the content page model.
  let popularMemoriesLift;
  let otherContentBlocks;
  if (contentPage.fields.blocks) {
    popularMemoriesLift = contentPage.fields.blocks.find(
      instanceOfCmsPopularMemoriesLiftBlock
    );
    otherContentBlocks = contentPage.fields.blocks.filter(
      (block) => !instanceOfCmsPopularMemoriesLiftBlock(block)
    );
  }

  return (
    <div data-cy="traveller-list-page">
      <SEO
        title={contentPage.fields.seoTitle || t('Järjestäjät')}
        canonicalPath={generatePath(Paths.SearchResults)}
        description={contentPage.fields.seoDescription || t('Järjestäjät')}
      />
      <Breadcrumbs items={[travellerListPageBreadcrumbItem]} />
      <Container>
        <Spacing bottom={6}>
          <Row>
            <Col size={[24, 24, 7]}>
              <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
                <DisplayOn large>
                  <H styling="h2">
                    <strong>{t('Järjestäjät')}</strong>
                  </H>
                  <Spacing top={2.5}>
                    {renderLabelledCounts(
                      travellersWithMemories.length,
                      memories.length,
                      destinations.length
                    )}
                  </Spacing>
                  <Spacing top={5}>
                    <H styling="h4">Golf x IMT.fi &mdash; Yhdessä enemmän</H>
                    <Paragraph small>
                      Golf x IMT.fi yhdistää intohimon golfiin ja vankan
                      matkailualan kokemuksen. Tarjoamme laadukkaita ja
                      vaivattomia golfelämyksiä Itämeren parhaille kentille
                      yhteistyössä, hyödyntäen IMT.fi:n asiantuntemusta, joka on
                      palvellut suomalaisia matkailijoita jo vuodesta 1991. Me
                      hoidamme järjestelyt, jotta sinä voit keskittyä
                      täydelliseen kierrokseen.
                    </Paragraph>
                  </Spacing>
                  <Spacing top={5}>
                    <TimMessage>
                      {t(
                        'Tässä esittelyssä kumppanimme ja matkanjärjestäjämme. Tutustu ammattilaisiin, jotka tekevät golfmatkastasi ikimuistoisen. ✌️😊'
                      )}
                    </TimMessage>
                  </Spacing>
                </DisplayOn>
              </StickyCol>
            </Col>
            <Col size={[0, 0, 1]} />
            <Col size={[24, 24, 16]}>
              <Row>
                <Col size={24}>
                  <DisplayOn small medium>
                    <Spacing bottom={4}>
                      {renderLabelledCounts(
                        travellersWithMemories.length,
                        memories.length,
                        destinations.length
                      )}
                    </Spacing>
                    <Spacing bottom={5}>
                      <H styling="h4">Golf x IMT.fi &mdash; Yhdessä enemmän</H>
                      <Paragraph small>
                        Golf x IMT.fi yhdistää intohimon golfiin ja vankan
                        matkailualan kokemuksen. Tarjoamme laadukkaita ja
                        vaivattomia golfelämyksiä Itämeren parhaille kentille
                        yhteistyössä, hyödyntäen IMT.fi:n asiantuntemusta, joka
                        on palvellut suomalaisia matkailijoita jo vuodesta 1991.
                        Me hoidamme järjestelyt, jotta sinä voit keskittyä
                        täydelliseen kierrokseen.
                      </Paragraph>
                    </Spacing>
                    <Spacing bottom={5}>
                      <TimMessage>
                        {t(
                          'Tässä esittelyssä kumppanimme ja matkanjärjestäjämme. Tutustu ammattilaisiin, jotka tekevät golfmatkastasi ikimuistoisen. ✌️😊'
                        )}
                      </TimMessage>
                    </Spacing>
                  </DisplayOn>
                  <Spacing top={columnTopSpacing.contentColumn} bottom={5}>
                    {popularMemoriesLift && (
                      <Spacing bottom={8}>
                        <ContentBlockList blocks={[popularMemoriesLift]} />
                      </Spacing>
                    )}
                    <HeadingWithCount
                      icon={'travel-traveller'}
                      iconSize={[2, 3, 3]}
                      text={t('Kaikki järjestäjät')}
                      renderCount={() => t(' — Uusin ensin')}
                    />
                  </Spacing>
                </Col>
              </Row>
              <Row>
                {travellersWithMemories.map((traveller) => {
                  return (
                    <Col key={traveller.sys.id} size={[24, 12, 12]}>
                      <Spacing bottom={5}>
                        <TravellerListPageCard
                          traveller={traveller}
                          memoryCount={traveller.memories.length}
                        />
                      </Spacing>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Spacing>
      </Container>
      <HorizontalLine expand={false} />
      <Spacing top={[8, 14, 14]} bottom={14}>
        {otherContentBlocks && <ContentBlockList blocks={otherContentBlocks} />}
      </Spacing>
    </div>
  );
};

TravellerListPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Järjestäjät')}</strong>
      </H>
    </DisplayOn>,
    page
  );

TravellerListPage.getInitialProps = async function () {
  const [contentPage, travellersWithMemories] = await Promise.all([
    fetchContentPage('matkaajat'),
    fetchAllTravellersWithMemories()
  ]);
  const httpErrorCode = !contentPage ? 404 : undefined;
  return { contentPage, travellersWithMemories, httpErrorCode };
};

export default TravellerListPage;
